import type { GoalLevel, OfferStationBoard, OfferStationView, OfferTargetType } from '../../models/attendantGoalOffer';
import type { AttendantGoalType } from '../../models/attendantGoalType';
import { chosenOption, extraGoalValue } from './levels';

type StationBoards = Pick<OfferStationView, 'placeId' | 'placeName' | 'targetType' | 'boardId' | 'boardName' | 'boards'>;
type StationRows = StationBoards & Pick<OfferStationView, 'recommended' | 'levelChosen' | 'options' | 'extraGoals'>;

/**
 * Quadros do posto depois da ativação (um por tipo de meta). Backend antigo manda só o principal
 * (`boardId`/`boardName`): vira uma lista de um. Sem quadro nenhum, lista vazia.
 */
export const stationBoards = (s: StationBoards): OfferStationBoard[] => {
  if (s.boards && s.boards.length > 0) return s.boards;
  return s.boardId ? [{ id: s.boardId, name: s.boardName ?? s.placeName, targetType: s.targetType }] : [];
};

/** Primeiro posto com quadros e os ids deles: é o que o monitor abre logo depois de ativar. `null` = nenhum quadro criado. */
export const firstStationBoards = (stations: StationBoards[]): { placeId: string; boardIds: string[] } | null => {
  for (const s of stations) {
    const boards = stationBoards(s);
    if (boards.length > 0) return { placeId: s.placeId, boardIds: boards.map((b) => b.id) };
  }
  return null;
};

export interface BoardRow {
  board: OfferStationBoard;
  /** Alvo do quadro; o quadro principal que veio sem alvo usa o tipo do posto. */
  type: AttendantGoalType | null;
  /** Nível da meta do quadro; `null` quando o backend não diz (meta adicional que não veio em `extraGoals`). */
  level: GoalLevel | null;
  value: number | null;
}

/**
 * Linhas da degustação ativa, uma por quadro do posto. O quadro principal (`boardId`) traz o nível que o dono escolheu;
 * os demais, o nível recomendado e o valor de `extraGoals` do mesmo tipo (sem eles, só o tipo e o link).
 */
export const boardRows = (s: StationRows): BoardRow[] => {
  const extras = new Map((s.extraGoals ?? []).map((g) => [g.targetType, g]));
  return stationBoards(s).map((board) => {
    const isMain = board.id === s.boardId;
    const type = board.targetType ?? (isMain ? s.targetType : null);
    if (isMain) {
      const option = chosenOption(s);
      return { board, type, level: option?.level ?? null, value: option?.value ?? null };
    }
    const extra = type ? extras.get(type as OfferTargetType) : undefined;
    return { board, type, level: extra ? extra.recommended : null, value: extra ? extraGoalValue(extra) : null };
  });
};
